import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import Badge from '@/components/base/Badge';
import Modal from '@/components/base/Modal';
import { downloadCSV } from '@/lib/csv';
import { todayMexico } from '@/lib/dates';

interface Producto {
  id: number;
  sku: string;
  name: string;
  description: string;
  category: string;
  unit_price: number;
  cost: number;
}

const categorias = [
  { key: 'Todas', label: 'Todas' },
  { key: 'licencias', label: 'Licencias' },
  { key: 'telecom', label: 'Telecomunicaciones' },
  { key: 'hardware', label: 'Hardware' },
  { key: 'servicios', label: 'Servicios' },
  { key: 'bundles', label: 'Bundles' },
];

const categoriaColors: Record<string, 'info' | 'warning' | 'success' | 'neutral'> = {
  licencias: 'info',
  telecom: 'warning',
  hardware: 'success',
  servicios: 'neutral',
  bundles: 'info',
};

const categoriaIcons: Record<string, string> = {
  licencias: 'ri-key-2-line',
  telecom: 'ri-signal-tower-line',
  hardware: 'ri-cpu-line',
  servicios: 'ri-tools-line',
  bundles: 'ri-stack-line',
};

export default function Catalogo() {
  const [productos, setProductos] = useState<Producto[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoriaFilter, setCategoriaFilter] = useState('Todas');

  const [showNewProductModal, setShowNewProductModal] = useState(false);
  const [showEditProductModal, setShowEditProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Producto | null>(null);
  const [newProduct, setNewProduct] = useState({
    sku: '',
    name: '',
    description: '',
    category: 'hardware',
    unit_price: '',
    cost: '',
  });
  const [productError, setProductError] = useState('');
  const [productSuccess, setProductSuccess] = useState('');

  useEffect(() => {
    fetchProductos();
  }, []);

  async function fetchProductos() {
    try {
      const { data, error } = await supabase
        .from('erp_products')
        .select('*')
        .order('category')
        .order('name');
      if (error) throw error;
      setProductos(data || []);
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  }

  const filtered = useMemo(() => {
    return productos.filter((p) => {
      const matchSearch = p.name.toLowerCase().includes(search.toLowerCase())
        || p.sku.toLowerCase().includes(search.toLowerCase())
        || p.description.toLowerCase().includes(search.toLowerCase());
      const matchCat = categoriaFilter === 'Todas' || p.category === categoriaFilter;
      return matchSearch && matchCat;
    });
  }, [productos, search, categoriaFilter]);

  const formatCurrency = (val: number) =>
    new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0, maximumFractionDigits: 6 }).format(val);

  const catLabel = (c: string) => {
    const found = categorias.find(x => x.key === c);
    return found?.label || c;
  };

  const statsPorCategoria = categorias.slice(1).map((cat) => ({
    nombre: cat.label,
    key: cat.key,
    count: productos.filter((p) => p.category === cat.key).length,
    icon: categoriaIcons[cat.key],
    color: categoriaColors[cat.key],
  }));

  function resetNewProductForm() {
    setNewProduct({
      sku: '',
      name: '',
      description: '',
      category: 'hardware',
      unit_price: '',
      cost: '',
    });
    setProductError('');
    setProductSuccess('');
  }

  function openEditModal(producto: Producto) {
    setEditingProduct(producto);
    setNewProduct({
      sku: producto.sku,
      name: producto.name,
      description: producto.description,
      category: producto.category,
      unit_price: String(producto.unit_price),
      cost: String(producto.cost || ''),
    });
    setProductError('');
    setProductSuccess('');
    setShowEditProductModal(true);
  }

  async function handleCreateProduct() {
    setProductError('');
    setProductSuccess('');

    if (!newProduct.sku.trim()) { setProductError('El SKU es obligatorio'); return; }
    if (!newProduct.name.trim()) { setProductError('El nombre es obligatorio'); return; }
    if (!newProduct.description.trim()) { setProductError('La descripción es obligatoria'); return; }
    if (!newProduct.unit_price || parseFloat(newProduct.unit_price) <= 0) { setProductError('El precio unitario debe ser mayor a 0'); return; }

    try {
      const { error } = await supabase
        .from('erp_products')
        .insert({
          sku: newProduct.sku.trim().toUpperCase(),
          name: newProduct.name.trim(),
          description: newProduct.description.trim(),
          category: newProduct.category,
          unit_price: parseFloat(newProduct.unit_price),
          cost: parseFloat(newProduct.cost) || 0,
        });

      if (error) throw error;

      setProductSuccess('Producto creado exitosamente');
      await fetchProductos();

      setTimeout(() => {
        setShowNewProductModal(false);
        resetNewProductForm();
      }, 800);

    } catch (err: any) {
      setProductError(err?.message || 'Error al crear el producto. Intente de nuevo.');
    }
  }

  async function handleEditProduct() {
    setProductError('');
    setProductSuccess('');

    if (!editingProduct) return;
    if (!newProduct.sku.trim()) { setProductError('El SKU es obligatorio'); return; }
    if (!newProduct.name.trim()) { setProductError('El nombre es obligatorio'); return; }
    if (!newProduct.description.trim()) { setProductError('La descripción es obligatoria'); return; }
    if (!newProduct.unit_price || parseFloat(newProduct.unit_price) <= 0) { setProductError('El precio unitario debe ser mayor a 0'); return; }

    try {
      const { error } = await supabase
        .from('erp_products')
        .update({
          sku: newProduct.sku.trim().toUpperCase(),
          name: newProduct.name.trim(),
          description: newProduct.description.trim(),
          category: newProduct.category,
          unit_price: parseFloat(newProduct.unit_price),
          cost: parseFloat(newProduct.cost) || 0,
        })
        .eq('id', editingProduct.id);

      if (error) throw error;

      setProductSuccess('Producto actualizado exitosamente');
      await fetchProductos();

      setTimeout(() => {
        setShowEditProductModal(false);
        setEditingProduct(null);
        resetNewProductForm();
      }, 800);

    } catch (err: any) {
      setProductError(err?.message || 'Error al actualizar el producto. Intente de nuevo.');
    }
  }

  function handleExportCSV() {
    if (filtered.length === 0) return;
    const headers = ['SKU', 'Nombre', 'Descripción', 'Categoría', 'Precio Unitario', 'Costo'];
    const rows = filtered.map(p => [
      p.sku,
      p.name,
      p.description,
      catLabel(p.category),
      String(p.unit_price),
      String(p.cost || 0),
    ]);
    downloadCSV(headers, rows, `catalogo-${todayMexico()}.csv`);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-foreground-500">Cargando catálogo...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-foreground-900">Catálogo de Productos y Servicios</h1>
          <p className="text-sm text-foreground-500 mt-0.5">{productos.length} ítems en el catálogo</p>
        </div>
        <button
          onClick={() => { resetNewProductForm(); setShowNewProductModal(true); }}
          className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 cursor-pointer transition-colors whitespace-nowrap self-start"
        >
          <i className="ri-add-line"></i>
          Nuevo Producto
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {statsPorCategoria.map((s) => (
          <button
            key={s.key}
            onClick={() => setCategoriaFilter(categoriaFilter === s.key ? 'Todas' : s.key)}
            className={`flex items-center gap-3 p-4 rounded-xl border transition-all cursor-pointer ${
              categoriaFilter === s.key
                ? 'bg-primary-50 border-primary-200 shadow-sm'
                : 'bg-white border-background-200 hover:border-background-300'
            }`}
          >
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
              categoriaFilter === s.key ? 'bg-primary-100' : 'bg-background-100'
            }`}>
              <i className={`${s.icon} text-sm ${categoriaFilter === s.key ? 'text-primary-600' : 'text-foreground-500'}`}></i>
            </div>
            <div className="text-left">
              <span className="text-lg font-bold text-foreground-900 block">{s.count}</span>
              <span className="text-[10px] text-foreground-500 uppercase tracking-wider">{s.nombre}</span>
            </div>
          </button>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            placeholder="Buscar por nombre, SKU o descripción..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 text-sm bg-white border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800 placeholder:text-foreground-400"
          />
        </div>
        <button onClick={handleExportCSV} className="flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium text-foreground-600 bg-white border border-background-200 rounded-lg hover:bg-background-50 cursor-pointer transition-colors whitespace-nowrap">
          <i className="ri-download-2-line"></i>
          Exportar CSV
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((p) => (
          <div key={p.id} className="bg-white rounded-xl border border-background-200 p-5 hover:border-background-300 transition-colors flex flex-col gap-3">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-[10px] text-foreground-500 bg-background-100 px-1.5 py-0.5 rounded">{p.sku}</span>
                  <Badge variant={categoriaColors[p.category] || 'neutral'}>{catLabel(p.category)}</Badge>
                </div>
                <h3 className="text-sm font-semibold text-foreground-800">{p.name}</h3>
                <p className="text-xs text-foreground-500 mt-1 line-clamp-2">{p.description}</p>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); openEditModal(p); }}
                className="w-7 h-7 rounded-md bg-background-100 hover:bg-background-200 flex items-center justify-center cursor-pointer transition-colors flex-shrink-0 ml-2"
                title="Editar producto"
              >
                <i className="ri-edit-line text-foreground-500 text-xs"></i>
              </button>
            </div>
            <div className="flex items-center justify-between pt-3 border-t border-background-100 mt-auto">
              <span className="text-lg font-bold text-foreground-900">{formatCurrency(Number(p.unit_price))}</span>
              <span className="text-xs text-foreground-500">Costo: {formatCurrency(Number(p.cost))}</span>
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="py-12 text-center">
          <i className="ri-store-2-line text-foreground-300 text-3xl"></i>
          <p className="text-sm text-foreground-500 mt-2">No se encontraron productos</p>
        </div>
      )}

      <Modal open={showNewProductModal} onClose={() => { setShowNewProductModal(false); resetNewProductForm(); }} title="Nuevo Producto" size="lg">
        <div className="space-y-4">
          {productSuccess && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2">
              <i className="ri-checkbox-circle-fill text-emerald-500"></i>
              <p className="text-xs text-emerald-700 font-medium">{productSuccess}</p>
            </div>
          )}
          {productError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
              <i className="ri-error-warning-fill text-red-500"></i>
              <p className="text-xs text-red-700 font-medium">{productError}</p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">SKU <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={newProduct.sku}
                onChange={(e) => setNewProduct({ ...newProduct, sku: e.target.value.toUpperCase() })}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800 font-mono"
                placeholder="Ej. GPS-MAG-001"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Nombre <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={newProduct.name}
                onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
                placeholder="Ej. GPS Magnético Teltonika FMB920"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground-700 mb-1">Descripción <span className="text-red-500">*</span></label>
            <textarea
              value={newProduct.description}
              onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800 resize-none"
              placeholder="Describe el producto o servicio..."
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground-700 mb-1">Categoría</label>
            <select
              value={newProduct.category}
              onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-700 cursor-pointer"
            >
              <option value="licencias">Licencias de Software</option>
              <option value="telecom">Telecomunicaciones (SIMs)</option>
              <option value="hardware">Hardware (GPS)</option>
              <option value="servicios">Servicios</option>
              <option value="bundles">Paquetes / Bundles</option>
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Precio Unitario Base (MXN) <span className="text-red-500">*</span></label>
              <input
                type="number"
                value={newProduct.unit_price}
                onChange={(e) => setNewProduct({ ...newProduct, unit_price: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
                placeholder="0.00"
                min="0"
                step="any"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Costo (MXN)</label>
              <input
                type="number"
                value={newProduct.cost}
                onChange={(e) => setNewProduct({ ...newProduct, cost: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
                placeholder="0.00"
                min="0"
                step="any"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => { setShowNewProductModal(false); resetNewProductForm(); }}
              className="px-4 py-2 text-xs font-medium text-foreground-600 bg-background-100 rounded-lg hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              onClick={handleCreateProduct}
              className="px-4 py-2 text-xs font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-save-line"></i>
              Guardar Producto
            </button>
          </div>
        </div>
      </Modal>

      <Modal open={showEditProductModal} onClose={() => { setShowEditProductModal(false); setEditingProduct(null); resetNewProductForm(); }} title="Editar Producto" size="lg">
        <div className="space-y-4">
          {productSuccess && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2">
              <i className="ri-checkbox-circle-fill text-emerald-500"></i>
              <p className="text-xs text-emerald-700 font-medium">{productSuccess}</p>
            </div>
          )}
          {productError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
              <i className="ri-error-warning-fill text-red-500"></i>
              <p className="text-xs text-red-700 font-medium">{productError}</p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">SKU <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={newProduct.sku}
                onChange={(e) => setNewProduct({ ...newProduct, sku: e.target.value.toUpperCase() })}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800 font-mono"
                placeholder="Ej. GPS-MAG-001"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Nombre <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={newProduct.name}
                onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
                placeholder="Ej. GPS Magnético Teltonika FMB920"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground-700 mb-1">Descripción <span className="text-red-500">*</span></label>
            <textarea
              value={newProduct.description}
              onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800 resize-none"
              placeholder="Describe el producto o servicio..."
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground-700 mb-1">Categoría</label>
            <select
              value={newProduct.category}
              onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 text-foreground-700 cursor-pointer"
            >
              <option value="licencias">Licencias de Software</option>
              <option value="telecom">Telecomunicaciones (SIMs)</option>
              <option value="hardware">Hardware (GPS)</option>
              <option value="servicios">Servicios</option>
              <option value="bundles">Paquetes / Bundles</option>
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Precio Unitario Base (MXN) <span className="text-red-500">*</span></label>
              <input
                type="number"
                value={newProduct.unit_price}
                onChange={(e) => setNewProduct({ ...newProduct, unit_price: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
                placeholder="0.00"
                min="0"
                step="any"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1">Costo (MXN)</label>
              <input
                type="number"
                value={newProduct.cost}
                onChange={(e) => setNewProduct({ ...newProduct, cost: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
                placeholder="0.00"
                min="0"
                step="any"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => { setShowEditProductModal(false); setEditingProduct(null); resetNewProductForm(); }}
              className="px-4 py-2 text-xs font-medium text-foreground-600 bg-background-100 rounded-lg hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              onClick={handleEditProduct}
              className="px-4 py-2 text-xs font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-save-line"></i>
              Actualizar Producto
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}